// Belgrade time for schedules and the monthly cap (DEVELOPMENT.md: all schedules are
// Europe/Belgrade), computed with Intl so it doesn't depend on the container's TZ.
export const ZONE = 'Europe/Belgrade';

export interface LocalParts {
  year: number;
  month: number; // 1–12
  day: number;
  hour: number;
  minute: number;
}

export function localParts(at: Date, zone = ZONE): LocalParts {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
}

/** The instant when the local clock in `zone` shows this date and time. */
export function zonedTime(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  zone = ZONE,
): Date {
  const wanted = Date.UTC(year, month - 1, day, hour, minute);
  let guess = wanted;
  for (let i = 0; i < 3; i++) {
    const p = localParts(new Date(guess), zone);
    const shown = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    guess += wanted - shown;
  }
  return new Date(guess);
}

/** Start of the current month (local), and of the next one. */
export function monthBounds(at: Date, zone = ZONE): { start: Date; next: Date } {
  const p = localParts(at, zone);
  const start = zonedTime(p.year, p.month, 1, 0, 0, zone);
  const next =
    p.month === 12
      ? zonedTime(p.year + 1, 1, 1, 0, 0, zone)
      : zonedTime(p.year, p.month + 1, 1, 0, 0, zone);
  return { start, next };
}

/** The local date as YYYY-MM-DD. */
export function localDate(at: Date, zone = ZONE): string {
  const p = localParts(at, zone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** A YYYY-MM-DD date moved by whole days. */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

/** The date whose daily is due: today from the daily time (HH:MM Belgrade), yesterday before. */
export function dueDate(now: Date, time = '06:00'): string {
  const today = localDate(now);
  const [h, m] = time.split(':').map(Number) as [number, number];
  const p = localParts(now);
  return p.hour * 60 + p.minute < h * 60 + m ? addDays(today, -1) : today;
}

/** Day of the week of a YYYY-MM-DD date: 0 Sunday … 6 Saturday. */
export function weekday(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** The start of the local week (Monday 00:00) that contains `at`. */
export function weekStart(at: Date, zone = ZONE): Date {
  const today = localDate(at, zone);
  const monday = addDays(today, -((weekday(today) + 6) % 7));
  const [y, m, d] = monday.split('-').map(Number) as [number, number, number];
  return zonedTime(y, m, d, 0, 0, zone);
}
